import React, { FC, useEffect, useRef, useState } from "react";
import interact from "interactjs";

import "./_slider-control.scss";

type SliderControlProps = {
  defaultValue: number;
  min: number;
  max: number;
  decimals: number;
  className?: string;
  onChange: (value: number) => void;
};

const SliderControl: FC<SliderControlProps> = ({
  defaultValue,
  min,
  max,
  decimals,
  className,
  onChange,
}) => {
  const [showValue, setShowValue] = useState<boolean>(false);

  const grabberRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!grabberRef.current) return;

    interact(grabberRef.current) // target elements with the 'slider' class
      .draggable({
        // make the element fire drag events
        origin: "self", // (0, 0) will be the element's top-left
        modifiers: [
          interact.modifiers.restrict({
            restriction: "self", // keep the drag coords within the element
          }),
        ],
      })
      .on("dragstart", () => {
        setShowValue(true);
      })
      .on("dragmove", function (event) {
        // call this listener on every dragmove
        const sliderWidth = interact.getElementRect(
          event.target.parentNode,
        ).width;
        const positionPercent = event.pageX / sliderWidth;
        const value = parseFloat(
          (min + positionPercent * (max - min)).toFixed(decimals),
        );

        event.target.style.paddingLeft = positionPercent * 100 + "%";
        event.target.setAttribute("data-value", value);
      })
      .on("dragend", (event) => {
        setShowValue(false);
        onChange(event.target.getAttribute("data-value"));
      });
  }, [decimals, max, min, onChange]);

  useEffect(() => {
    if (!grabberRef.current) return;

    grabberRef.current.style.paddingLeft = `${
      ((defaultValue - min) / (max - min)) * 100
    }%`;
  }, [defaultValue, min, max]);

  return (
    <div
      className={`slider-control ${className ?? ""} ${
        showValue ? "slider-control--show-value" : ""
      }`}
    >
      <div className="slider-control__rail" ref={grabberRef} />
    </div>
  );
};

export default SliderControl;
